import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Counter/Stepper Component
 *
 * A reusable numeric counter with increment/decrement buttons.
 * Supports both standalone usage and reactive forms via ControlValueAccessor.
 *
 * @example
 * <!-- Standalone usage -->
 * <app-counter
 *   label="Minimum nights"
 *   [(value)]="minNights"
 *   [min]="1"
 *   [max]="7">
 * </app-counter>
 *
 * @example
 * <!-- With reactive forms -->
 * <app-counter
 *   label="Rooms available"
 *   formControlName="availableRooms"
 *   [min]="0"
 *   [max]="totalRooms">
 * </app-counter>
 */
@Component({
  selector: 'app-counter',
  templateUrl: './counter.component.html',
  styleUrls: ['./counter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CounterComponent),
      multi: true
    }
  ]
})
export class CounterComponent implements ControlValueAccessor {
  /** Label displayed above the counter */
  @Input() label = '';

  /** Minimum allowed value */
  @Input() min = 0;

  /** Maximum allowed value (null = unlimited) */
  @Input() max: number | null = null;

  /** Step value for increment/decrement */
  @Input() step = 1;

  /** Whether the counter is disabled */
  @Input() disabled = false;

  /** Whether the counter is readonly (displays value only) */
  @Input() readonly = false;

  /** Current value for standalone usage */
  @Input()
  get value(): number {
    return this._value;
  }
  set value(val: number | undefined | null) {
    const newVal = val ?? 0;
    if (newVal !== this._value) {
      this._value = newVal;
      this.valueChange.emit(newVal);
      this.onChange(newVal);
    }
  }

  /** Emits when value changes (for two-way binding) */
  @Output() valueChange = new EventEmitter<number>();

  protected _value = 0;

  // ControlValueAccessor callbacks
  private onChange: (value: number) => void = () => {};
  protected onTouched: () => void = () => {};

  /** Whether decrement button should be disabled */
  get canDecrement(): boolean {
    return !this.disabled && !this.readonly && this._value > this.min;
  }

  /** Whether increment button should be disabled */
  get canIncrement(): boolean {
    if (this.disabled || this.readonly) return false;
    if (this.max === null) return true;
    return this._value < this.max;
  }

  /** Increment the value */
  increment(): void {
    if (!this.canIncrement) return;

    const newValue = this._value + this.step;
    if (this.max !== null && newValue > this.max) {
      this._value = this.max;
    } else {
      this._value = newValue;
    }

    this.valueChange.emit(this._value);
    this.onChange(this._value);
    this.onTouched();
  }

  /** Decrement the value */
  decrement(): void {
    if (!this.canDecrement) return;

    const newValue = this._value - this.step;
    if (newValue < this.min) {
      this._value = this.min;
    } else {
      this._value = newValue;
    }

    this.valueChange.emit(this._value);
    this.onChange(this._value);
    this.onTouched();
  }

  /** Handle direct input from the editable field */
  onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    let newValue = parseInt(input.value, 10);

    if (isNaN(newValue)) {
      newValue = this.min;
    }

    // Clamp to min/max
    if (newValue < this.min) newValue = this.min;
    if (this.max !== null && newValue > this.max) newValue = this.max;

    this._value = newValue;
    input.value = String(newValue);
    this.valueChange.emit(this._value);
    this.onChange(this._value);
    this.onTouched();
  }

  // ControlValueAccessor implementation
  writeValue(value: number): void {
    this._value = value ?? 0;
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
